import * as React from "react";
import axios from "axios";
import { Link as RouterLink, useParams } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import { appConstants } from "../_constants/app.constants";
import { useTournamentAccess } from "../hooks/useTournamentAccess";
import { useTournamentPageView } from "../analytics/useTournamentPageView";
import { saveTournamentShields } from "../_utils/shieldUtils";
import {
  buildImageMap,
  collectImagesFromGroups,
  dedupeTeamImages,
  patchTeamShield,
  readCachedTournamentImages,
  resolveTournamentIdForViewer,
} from "../_utils/tournamentViewUtils";
import { Button, EmptyState, LoadingState, Toast } from "../ui";
import "./groups.css";

const GroupTableRow = ({ groupTeam, index, prevData, fewGroups }) => {
  const team = groupTeam.team ?? {};
  const isUpdated =
    prevData &&
    (prevData.points !== groupTeam.points ||
      prevData.goals !== groupTeam.goals ||
      prevData.goalsReceived !== groupTeam.goalsReceived ||
      prevData.matchsPlayed !== groupTeam.matchsPlayed);

  const formatTeamName = (name) => {
    if (!name) return "";
    if (fewGroups) return name;
    const replacements = {
      "Club Deportivo": "C.D.",
      "Club Atletico": "C.A.",
      "Futbol Club": "F.C.",
      "Union Deportiva": "U.D.",
      "Real Club": "R.C.",
      "Sociedad Deportiva": "S.D.",
      "Sociedad Cultural": "S.C.",
      Atletico: "Atl.",
      Deportivo: "Dep.",
      Racing: "Rac.",
      Sporting: "Sport.",
    };

    let shortName = name;
    Object.entries(replacements).forEach(([full, abbr]) => {
      shortName = shortName.replace(new RegExp(full, "gi"), abbr);
    });
    return shortName;
  };

  const teamNameClass = fewGroups ? "team-name wide-name" : "team-name";
  const dataColumnsClass = fewGroups ? "number-column wide-column" : "number-column";

  return (
    <tr key={team.id ?? index} className={isUpdated ? "updated-row" : ""}>
      <td className="position-cell">{index + 1}</td>
      <td className="logo-container">
        {team.shield ? <img className="logo" src={team.shield} alt={`${team.name ?? "Equipo"} shield`} loading="lazy" /> : null}
      </td>
      <td className={teamNameClass} title={team.name ?? ""}>
        {formatTeamName(team.name)}
      </td>
      <td className={`points-cell ${dataColumnsClass}`}>{groupTeam.points ?? 0}</td>
      <td className={dataColumnsClass}>{groupTeam.goals ?? 0}</td>
      <td className={dataColumnsClass}>{groupTeam.goalsReceived ?? 0}</td>
      <td className={dataColumnsClass}>{groupTeam.matchsPlayed ?? 0}</td>
    </tr>
  );
};

export default function ViewGroups() {
  const { tournamentId } = useParams();
  const [tId, setTournamentId] = useState(tournamentId ?? null);
  const { canManage, loaded: accessLoaded } = useTournamentAccess(tId);
  const [groups, setGroups] = useState([]);
  const [prevGroups, setPrevGroups] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  useTournamentPageView(tId, "groups");

  useEffect(() => {
    let active = true;
    const resolveTournament = async () => {
      setLoading(true);
      const resolvedId = await resolveTournamentIdForViewer(tournamentId);
      if (!active) return;
      setTournamentId(resolvedId);
      if (!resolvedId) {
        setGroups([]);
        setLoadError("");
        setLoading(false);
      }
    };
    resolveTournament();
    return () => {
      active = false;
    };
  }, [tournamentId]);

  const hydrateGroupsWithShields = useCallback(
    (fetchedGroups) => {
      const source = Array.isArray(fetchedGroups) ? fetchedGroups : [];
      const cachedImages = readCachedTournamentImages(tId);
      const freshImages = collectImagesFromGroups(source);
      const mergedImages = dedupeTeamImages([...cachedImages, ...freshImages]);

      if (mergedImages.length > 0 && tId) saveTournamentShields(tId, mergedImages);

      const imageMap = buildImageMap(mergedImages);
      const hydrated = source.map((group) => ({
        ...group,
        tournamentGroupTeams: (group.tournamentGroupTeams ?? []).map((teamRow) => ({
          ...teamRow,
          team: patchTeamShield(teamRow.team, imageMap),
        })),
      }));

      setPrevGroups(groups);
      setGroups(hydrated);
      setLoading(false);
    },
    [groups, tId]
  );

  useEffect(() => {
    if (!tId) return undefined;

    let cancelled = false;
    const fetchGroups = async () => {
      try {
        setLoadError("");
        const res = await axios.get(`${appConstants.API_URL}/api/tournamentgroups/${tId}/teams/ordered`);
        if (!cancelled) hydrateGroupsWithShields(res.data);
      } catch (err) {
        if (cancelled) return;
        const status = err?.response?.status;
        if (status === 403 || status === 404) setLoadError("No tienes acceso público a este torneo.");
        else setLoadError("No se pudieron cargar los grupos.");
        setGroups([]);
        setLoading(false);
      }
    };

    fetchGroups();
    const interval = setInterval(fetchGroups, 10000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [hydrateGroupsWithShields, tId]);

  const findPrevTeamData = (groupId, teamId) => {
    if (!prevGroups || !teamId) return null;
    const prevGroup = prevGroups.find((g) => g.id === groupId);
    if (!prevGroup) return null;
    return (prevGroup.tournamentGroupTeams ?? []).find((t) => t.team?.id === teamId) ?? null;
  };

  if (loading && !groups.length) return <LoadingState />;

  if (!loading && !tId) {
    return (
        <EmptyState
        title="Falta el torneo en la URL"
        description="Abre esta vista desde el menú del torneo; la ruta debe incluir el identificador del torneo. No se elige ningún torneo automáticamente al abrir /viewgroups sin id."
      />
    );
  }

  if (!loading && loadError) {
    return (
      <div className="mx-auto max-w-xl p-4">
        <Toast kind="warning">{loadError}</Toast>
      </div>
    );
  }

  if (!loading && groups.length === 0) {
    const adminCopy = accessLoaded && canManage;
    return (
      <EmptyState
        title="No hay grupos en este torneo"
        description={
          adminCopy
            ? "Crea grupos y asigna equipos desde la configuración del torneo."
            : "Cuando el organizador configure la fase de grupos, las tablas aparecerán aquí."
        }
        action={
          adminCopy && tId ? (
            <RouterLink to={`/tournamentactions/${tId}/setup#grupos`}>
              <Button variant="gold">Configurar grupos</Button>
            </RouterLink>
          ) : null
        }
      />
    );
  }

  const calculateGridLayout = (count) => {
    if (count <= 4) return count <= 2 ? "lg:grid-cols-2" : "sm:grid-cols-2";
    if (count <= 6) return "sm:grid-cols-2 lg:grid-cols-3";
    return "sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4";
  };

  const pointsLabel = groups.length > 4 ? "PTS" : "Puntos";
  const dataColumnsClass = groups.length <= 4 ? "number-column wide-column" : "number-column";

  return (
    <div className="groups-container min-h-screen w-full overflow-auto p-1 sm:p-2">
      <div className={`grid gap-1.5 ${calculateGridLayout(groups.length)}`}>
        {groups.map((group) => (
          <section key={group.id} className="group-card overflow-hidden">
            <div className="group-header">{group.name}</div>
            <div className="group-content">
              <table className={`teams-table ${groups.length > 6 ? "many-groups" : ""}`}>
                <thead>
                  <tr>
                    <th className="position-th">#</th>
                    <th className="logo-th" />
                    <th className={groups.length <= 4 ? "name-th wide-name" : "name-th"}>Equipo</th>
                    <th className={dataColumnsClass}>{pointsLabel}</th>
                    <th className={dataColumnsClass}>G.F.</th>
                    <th className={dataColumnsClass}>G.E.</th>
                    <th className={dataColumnsClass}>PJ</th>
                  </tr>
                </thead>
                <tbody>
                  {(group.tournamentGroupTeams ?? []).map((team, index) => (
                    <GroupTableRow
                      key={team?.team?.id ?? `${group.id}-${index}`}
                      groupTeam={team}
                      index={index}
                      prevData={findPrevTeamData(group.id, team?.team?.id)}
                      fewGroups={groups.length <= 4}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
